import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Button, Typography } from "@mui/material";
import MuiToggleButton from "@mui/material/ToggleButton";
import ClearIcon from "@mui/icons-material/Clear";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import {
  parseSelectedProjectIds,
  parseSelectedTagIds,
  storeSelectedProjectIds,
  storeSelectedTagIds,
} from "../util/filterHandler";
import * as projectActions from "../actions/projectActions";
import { ProjectActions } from "../actions/projectActions";
import { bindActionCreators } from "../actions/actionCreators";
import { AppContext } from "../models/applicationState";
import { Context } from "./context";

export interface ToolbarTogglesProps {
  onArchived?: (archived: boolean) => void;
  onCompleted?: (completed: boolean) => void;
  onInactive?: (inactive: boolean) => void;
  onShowOnlyPrioritized?: (onlyPrioritized: boolean) => void;
  onShowOnlySelfAssignedQuicktasks?: (onlySelfAssigned: boolean) => void;
  hideItemStatusToggles?: boolean;
}

export const ToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  border: "none",
  fontSize: "0.75rem",
  padding: "4px 8px",
  height: 30,
  whiteSpace: "nowrap",
  lineHeight: 1.2,
  "&.MuiToggleButton-root": {
    color: theme.palette.primary.main,
  },
  "&.Mui-selected": {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    "&:hover": {
      backgroundColor: theme.palette.action.focus,
    },
  },
  "&:hover": {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ToolbarToggles: FC<ToolbarTogglesProps> = (
  props,
): ReactElement => {
  const { t } = useTranslation();
  const [archived, setArchived] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showOnlyPrioritized, setShowOnlyPrioritized] = useState(false);
  const [showOnlySelfAssignedQuicktasks, setShowOnlySelfAssignedQuicktasks] =
    useState(false);

  const appContext = useContext<AppContext>(Context);
  const [projectFilters, setProjectFilters] = useState(
    parseSelectedProjectIds().length + parseSelectedTagIds().length,
  );

  const actions = useMemo(
    () => ({
      projectFilters: bindActionCreators(
        projectActions,
        appContext.dispatch,
      ) as unknown as ProjectActions,
    }),
    [appContext.dispatch],
  );

  useEffect(() => {
    setProjectFilters(
      parseSelectedProjectIds().length + parseSelectedTagIds().length,
    );
  }, [appContext.state.selectedProjectFilters]);

  const handleClearProjectFilters = () => {
    storeSelectedProjectIds([]);
    storeSelectedTagIds([]);
    setProjectFilters(0);
    actions.projectFilters.setSelectedProjectFilters({
      projectIds: [],
      tagIds: [],
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.25,
        alignItems: "center",
      }}
    >
      {props.onArchived && (
        <ToggleButton
          value="archived"
          selected={archived}
          onChange={() => {
            const newArchived = !archived;
            setArchived(newArchived);
            props.onArchived && props.onArchived(newArchived);
          }}
          color="primary"
        >
          {archived ? (
            <>
              <CheckBoxIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("library.showArchived")}
              </Typography>
            </>
          ) : (
            <>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("library.showArchived")}
              </Typography>
            </>
          )}
        </ToggleButton>
      )}
      {!props.hideItemStatusToggles && props.onCompleted && (
        <ToggleButton
          value="completed"
          selected={showCompleted}
          onChange={() => {
            const newCompleted = !showCompleted;
            setShowCompleted(newCompleted);
            props.onCompleted && props.onCompleted(newCompleted);
          }}
        >
          {!showCompleted ? (
            <>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showCompleted")}
              </Typography>
            </>
          ) : (
            <>
              <CheckBoxIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showCompleted")}
              </Typography>
            </>
          )}
        </ToggleButton>
      )}
      {!props.hideItemStatusToggles && props.onInactive && (
        <ToggleButton
          value="inactive"
          selected={showInactive}
          onChange={() => {
            const newInactive = !showInactive;
            setShowInactive(newInactive);
            props.onInactive && props.onInactive(newInactive);
          }}
        >
          {!showInactive ? (
            <>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showInactive")}
              </Typography>
            </>
          ) : (
            <>
              <CheckBoxIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showInactive")}
              </Typography>
            </>
          )}
        </ToggleButton>
      )}
      {!props.hideItemStatusToggles && props.onShowOnlyPrioritized && (
        <ToggleButton
          value="prioritized"
          selected={showOnlyPrioritized}
          onChange={() => {
            const newShowOnlyPrioritized = !showOnlyPrioritized;
            setShowOnlyPrioritized(newShowOnlyPrioritized);
            props.onShowOnlyPrioritized &&
              props.onShowOnlyPrioritized(newShowOnlyPrioritized);
          }}
        >
          {!showOnlyPrioritized ? (
            <>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showOnlyPrioritized")}
              </Typography>
            </>
          ) : (
            <>
              <CheckBoxIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showOnlyPrioritized")}
              </Typography>
            </>
          )}
        </ToggleButton>
      )}
      {props.onShowOnlySelfAssignedQuicktasks && (
        <ToggleButton
          value="selfAssignedQuicktasks"
          selected={showOnlySelfAssignedQuicktasks}
          onChange={() => {
            const newShowOnlySelfAssignedQuicktasks =
              !showOnlySelfAssignedQuicktasks;
            setShowOnlySelfAssignedQuicktasks(
              newShowOnlySelfAssignedQuicktasks,
            );
            props.onShowOnlySelfAssignedQuicktasks &&
              props.onShowOnlySelfAssignedQuicktasks(
                newShowOnlySelfAssignedQuicktasks,
              );
          }}
        >
          {!showOnlySelfAssignedQuicktasks ? (
            <>
              <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showSelfAssigned") ??
                  "Show Only Self-Assigned Quicktasks"}
              </Typography>
            </>
          ) : (
            <>
              <CheckBoxIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
              <Typography variant="body2">
                {t("toolbar.showSelfAssigned") ??
                  "Show Only Self-Assigned Quicktasks"}
              </Typography>
            </>
          )}
        </ToggleButton>
      )}
      {projectFilters > 0 && (
        <Button
          sx={theme => ({
            background: theme.palette.action.selected,

            "&:hover": {
              backgroundColor: theme.palette.action.focus,
            }
          })}
          startIcon={<ClearIcon />}
          onClick={handleClearProjectFilters}
        >
          {t("toolbar.clear_project_filter", { count: projectFilters })}
        </Button>
      )}
    </Box>
  );
};
